/*************************************************************************
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import UAParser from "ua-parser-js";
import { isVersionGreater, isVersionLesser } from "./Common";

export const browsers = {
  CHROME: "chrome",
  SAFARI: "safari",
  MOBILE_SAFARI: "mobile safari",
  FIREFOX: "firefox",
  IE: "ie",
  EDGE: "edge"
};

export const getBrowser = () => {
  const parser = new UAParser();
  return parser.getBrowser();
};

export const isSafari = () => {
  const browserInfo = getBrowser();
  const browserName = browserInfo.name.toLowerCase();
  return (
    browserName === browsers.SAFARI || browserName === browsers.MOBILE_SAFARI
  );
};

export const isSafari13 = () => {
  const browserInfo = getBrowser();
  return (
    browserInfo.name.toLowerCase() === browsers.SAFARI &&
    browserInfo.version.substr(0, 2) === "13"
  );
};

export const isSafari10 = () => {
  const browserInfo = getBrowser();
  return (
    browserInfo.name.toLowerCase() === browsers.SAFARI &&
    browserInfo.version.substr(0, 2) === "10"
  );
};

export const isSafari9 = () => {
  const browserInfo = getBrowser();
  return (
    browserInfo.name.toLowerCase() === browsers.SAFARI &&
    browserInfo.version.substr(0, 1) === "9"
  );
};

export const isEditRestrictedOnSafari = () => {
  const { version } = getBrowser();
  return isSafari() && !isVersionGreater(version, "13.1");
};

export const isFirefox = () => {
  const browserInfo = getBrowser();
  return browserInfo.name.toLowerCase() === browsers.FIREFOX;
};

export const isChrome = () => {
  const browserInfo = getBrowser();
  return browserInfo.name.toLowerCase() === browsers.CHROME;
};

export const isChrome64 = () => {
  const browserInfo = getBrowser();
  return (
    browserInfo.name.toLowerCase() === browsers.CHROME &&
    browserInfo.version.substr(0, 2) === "64"
  );
};

export const isIE = () => {
  const browserInfo = getBrowser();
  return browserInfo.name.toLowerCase() === browsers.IE;
};

export const isEdge = () => {
  const browserInfo = getBrowser();
  return browserInfo.name.toLowerCase() === browsers.EDGE;
};

export const isEditRestrictedOnEdge = () => {
  const browserInfo = getBrowser();
  return (
    browserInfo.name.toLowerCase() === browsers.EDGE &&
    isVersionLesser(browserInfo.version, "79")
  );
};

export const isEdgeHtml = () => {
  const browserInfo = getBrowser();
  // stable version of Edge < 44 version shows version string as 18.x
  if (browserInfo.name.toLowerCase() === browsers.EDGE) {
    const majorVersion = browserInfo.major
      ? parseInt(browserInfo.major, 10)
      : parseInt(browserInfo.version.split(".")[0], 10);
    return majorVersion <= 18;
  }
  return false;
};

export const isEdgeChromium = () => {
  const browserInfo = getBrowser();
  return (
    browserInfo.name.toLowerCase() === browsers.EDGE &&
    parseInt(browserInfo.version.substr(0, 2), 10) >= 79
  );
};
